import axios from 'axios';
import { setLastBusinessId } from './BusinessHelpers.js';
import * as Toast from '~/lib/Toast';
import * as Sentry from '@sentry/react';

type TokenFetchFunction = () => Promise<string>;

let getAccessTokenSilently: TokenFetchFunction | null = null;

export function setGetAccessToken(fn: TokenFetchFunction) {
    getAccessTokenSilently = fn;
}

let businessId: string | null = null;

export function setBusinessId(id: string) {
    businessId = id;
    setLastBusinessId(id);
}

export function canCallAuthenticatedApi() {
    return !!businessId && typeof getAccessTokenSilently === 'function';
}

export function getBusinessId() {
    return businessId;
}

export function businessApiRoute(route: string) {
    if (!businessId) {
        console.error(
            'Error, attempting business API without a business ID set',
        );
    }
    return `/api/business/${businessId}${route}`;
}

export function userApiRoute(route: string) {
    return `/api${route}`;
}

async function getHeaders() {
    if (getAccessTokenSilently == null) {
        throw new Error('getAccessTokenSilently empty');
    }
    const accessToken = await getAccessTokenSilently();
    // console.log(`@@@@ TOK: ${accessToken}`)
    return {
        Authorization: `Bearer ${accessToken}`,
        Accept: 'application/json',
    };
}

let lastAccessDeniedAlert = false;
export async function get(
    route: string,
    params?: { [key: string]: string | number },
    opts?: any,
) {
    const start = new Date();
    try {
        const res = await axios.get(route, {
            params,
            headers: await getHeaders(),
        });

        return res.data;
    } catch (e) {
        const code = e?.response?.status;

        const duration = new Date() - start;
        if (duration > 15 * 1000 && code === 500) {
            Sentry.captureMessage(`API get to ${route} took ${duration}ms`);
        }

        if (code === 403) {
            if (
                !lastAccessDeniedAlert ||
                new Date() - lastAccessDeniedAlert < 1000
            ) {
                Toast.error('Access denied, please try a different page');
                lastAccessDeniedAlert = new Date();
            }

            throw e;
        }
        throw e;
    }
}

export async function post(
    route: string,
    data: Parameters<typeof axios.post>[1],
) {
    const start = new Date();
    try {
        const res = await axios.post(route, data, {
            headers: await getHeaders(),
        });

        return res.data;
    } catch (e) {
        const code = e?.response?.status;

        const duration = new Date() - start;
        if (duration > 15 * 1000 && code === 500) {
            Sentry.captureMessage(`API post to ${route} took ${duration}ms`);
        }

        throw e;
    }
}

export async function deleteRequest(route: string) {
    const start = new Date();
    try {
        const res = await axios.delete(route, {
            headers: await getHeaders(),
        });

        return res.data;
    } catch (e) {
        const code = e?.response?.status;

        const duration = new Date() - start;
        if (duration > 15 * 1000 && code === 500) {
            Sentry.captureMessage(`API delete to ${route} took ${duration}ms`);
        }

        throw e;
    }
}

export async function put(
    route: string,
    data: Parameters<typeof axios.put>[1],
) {
    const start = new Date();
    try {
        const res = await axios.put(route, data, {
            headers: await getHeaders(),
        });

        return res.data;
    } catch (e) {
        const code = e?.response?.status;

        const duration = new Date() - start;
        if (duration > 15 * 1000 && code === 500) {
            Sentry.captureMessage(`API put to ${route} took ${duration}ms`);
        }

        throw e;
    }
}
